<template>
  <section class="w-full py-16 md:py-20 lg:py-24 bg-white">
    <div class="max-w-[1440px] mx-auto px-6 md:px-8 lg:px-12">
      <!-- Header Section -->
      <div ref="headerRef" class="text-center mb-12 md:mb-16 opacity-0 translate-y-8">
        <!-- Title with blue dot -->
        <div class="flex items-center justify-center gap-3 mb-4">
          <div class="w-3 h-3 rounded-full bg-[#0A2F70]"></div>
          <!-- Titre - Mobile -->
          <H3Title className="md:hidden text-2xl sm:text-3xl font-extrabold text-[#0F7F5C] uppercase tracking-tight">
            TYPE DE SIGNALEMENT
          </H3Title>
          
          <!-- Titre - Desktop -->
          <H2Title className="hidden md:block text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0F7F5C] uppercase tracking-tight">
            TYPE DE SIGNALEMENT
          </H2Title>
        </div>
        
        <!-- Subtitle - Mobile -->
        <PContent className="md:hidden text-sm sm:text-base text-gray-700 font-normal max-w-2xl mx-auto">
          Identifiez le type d'irrégularité que vous souhaitez signaler
        </PContent>
        
        <!-- Subtitle - Desktop -->
        <H4Title className="hidden md:block text-base md:text-lg lg:text-xl text-gray-700 font-normal max-w-2xl mx-auto">
          Identifiez le type d'irrégularité que vous souhaitez signaler
        </H4Title>
      </div>

      <!-- Cards Grid: 3 columns × 2 rows -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
        <div
          v-for="(card, index) in signalementTypes"
          :key="card.id"
          class="type-card group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-2 hover:border-[#0F7F5C]/30 transition-all duration-500 p-6 md:p-8 relative overflow-hidden"
          :ref="(el) => setCardRef(el, index)"
        >
          <!-- Decorative Background Shape -->
          <div class="absolute -right-4 -top-4 w-24 h-24 bg-[#0A2F70]/5 rounded-full blur-2xl group-hover:bg-[#0F7F5C]/10 transition-colors duration-500"></div>

          <div class="relative z-10">
            <div class="mb-8 inline-flex items-center justify-center p-3 rounded-xl bg-gray-50 group-hover:bg-[#0A2F70]/5 transition-colors duration-500">
              <component
                :is="card.icon"
                v-if="!card.secondaryIcon"
                class="w-10 h-10 md:w-12 md:h-12 text-[#0A2F70] group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500"
              />
              <div v-else class="relative">
                <component
                  :is="card.icon"
                  class="w-10 h-10 md:w-12 md:h-12 text-[#0A2F70] group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500"
                />
                <component
                  :is="card.secondaryIcon"
                  class="w-4 h-4 md:w-5 md:h-5 text-[#0A2F70] absolute -top-1 -right-1"
                />
              </div>
            </div>
            
            <div class="">
              <h3 class="text-lg md:text-xl font-bold text-[#0A2F70] mb-3 md:mb-4 group-hover:text-[#0F7F5C] transition-colors duration-300">
                {{ card.title }}
              </h3>
              <p class="text-sm md:text-base text-gray-700 leading-relaxed">
                {{ card.description }}
              </p>
            </div>

          </div>
        </div>
      </div>

      <!-- Bottom Text -->
      <div ref="footerRef" class="text-center opacity-0 translate-y-4">
        <p class="text-sm md:text-base text-gray-700 max-w-3xl mx-auto italic">
          Vous ne trouvez pas votre cas ? Tous les types de signalements sont acceptés et examinés.
        </p>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { gsap } from 'gsap'
import type { Component } from 'vue'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { H2Title, H3Title, H4Title, PContent } from '@/components/atoms/text'
import { DollarSign, Briefcase, Shield, Scale, User, AlertTriangle } from 'lucide-vue-next'

defineOptions({
  name: 'TypeSignalementSection'
})

interface SignalementType {
  id: number
  title: string
  description: string
  icon: Component
  secondaryIcon?: Component
}

const signalementTypes: SignalementType[] = [
  {
    id: 1,
    title: 'Fraude financière',
    description: 'Détournement de fonds, corruption, blanchiment d\'argent...',
    icon: DollarSign
  },
  {
    id: 2,
    title: 'Conflits d\'intérêts',
    description: 'Favoritisme, collusion, intérêts personnels non declarés…',
    icon: Briefcase
  },
  {
    id: 3,
    title: 'Marchés Publics',
    description: 'Pots de vin , fractionnement injustifié des marchés, surfacturation, abus...',
    icon: Shield
  },
  {
    id: 4,
    title: 'Irrégularités de gouvernance',
    description: 'Non respect des textes et procédures, défaillance du contrôle interne...',
    icon: Scale
  },
  {
    id: 5,
    title: 'Harcèlement & Discrimination',
    description: 'Dévalorisation professionnelle, discrimination basée sur le genre...',
    icon: User
  },
  {
    id: 6,
    title: 'Autres Irrégularités',
    description: 'Falsification de documents, transactions frauduleuses...',
    icon: AlertTriangle
  }
]

const headerRef = ref<HTMLElement | null>(null)
const footerRef = ref<HTMLElement | null>(null)
const cardRefs = ref<HTMLElement[]>([])
let observer: IntersectionObserver | null = null

const setCardRef = (el: Element | null, index: number) => {
  if (!el) return
  cardRefs.value[index] = el as HTMLElement
}

onMounted(() => {
  // Setup entrance animations with GSAP
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return

        if (entry.target === headerRef.value) {
          gsap.to(headerRef.value, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power3.out'
          })
          observer?.unobserve(headerRef.value)
        } else if (entry.target === footerRef.value) {
          gsap.to(footerRef.value, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: 0.2,
            ease: 'power3.out'
          })
          observer?.unobserve(footerRef.value)
        } else {
          // Cards animation
          const index = cardRefs.value.findIndex((card) => card === entry.target)
          if (index >= 0) {
            // We use a small delay based on index if we want staggered effect 
            // but since they might intersect at different times, we can also use a simple fade in
            gsap.to(entry.target, {
              opacity: 1,
              y: 0,
              scale: 1,
              duration: 0.8,
              ease: 'back.out(1.2)',
              force3D: true
            })
            observer?.unobserve(entry.target)
          }
        }
      })
    },
    {
      threshold: 0.15,
      rootMargin: '0px 0px -50px 0px',
    }
  )

  if (headerRef.value) observer.observe(headerRef.value)
  if (footerRef.value) observer.observe(footerRef.value)
  cardRefs.value.forEach((card) => {
    if (card) {
      // Set initial state for GSAP
      gsap.set(card, { opacity: 0, y: 40, scale: 0.95 })
      observer?.observe(card)
    }
  })
})

onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
})
</script>

<style scoped>
.type-card {
  will-change: transform, opacity, box-shadow;
}

@media (prefers-reduced-motion: reduce) {
  .type-card, .text-center {
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
  }
}
</style>

